import React, { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { collect } from 'collect.js';
import { useOngoingOrders } from '../../providers/OngoingOrdersProvider';
import { useHelpers } from '../../providers/HelpersProvider';
import { Button, Card, Flex, Tag } from 'antd';
import classnames from 'classnames';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  LoadingOutlined,
  MessageOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import OrderMethodIcon, { Sizes } from '../order/OrderMethodIcon';
import TablePill from '../order/TablePill';
import useTableLabel from '../../hooks/useTableLabel';
import OrderHelpers from '../../services/helpers/OrderHelpers';
import { DateTime } from 'luxon';

export default function OrderCard({ order }) {
  const { t } = useTranslation(null, { keyPrefix: 'Components:Cards:OrderCard' });
  const tableLabel = useTableLabel();

  const { formatCurrency, formatTime } = useHelpers();
  const { toggleOrderDetailsModal, toggleCompleteOrderModal } = useOngoingOrders();

  const pickupTime = DateTime.fromISO(order.estimated_pickup_time);
  const hasPickupTime = pickupTime.isValid;
  const isToday = useMemo(
    () => DateTime.fromISO(order.estimated_pickup_time).hasSame(DateTime.local(), 'day'),
    [order.estimated_pickup_time],
  );
  const isExternalDelivery = OrderHelpers.isExternalDelivery(order);
  const isPendingConfirmation = OrderHelpers.isPendingConfirmation(order);
  const isPendingDriver = OrderHelpers.isPendingDriver(order);
  const isDeliveryDispatchRetrying = OrderHelpers.isDeliveryDispatchRetrying(order);
  const isDeliveryDispatchFailed = OrderHelpers.isDeliveryDispatchFailed(order);
  const isScheduledOrderWithPendingPreparation = OrderHelpers.isScheduledOrderWithPendingPreparation(order);
  const isComplete = OrderHelpers.isComplete(order);
  const isOngoing = OrderHelpers.isOngoing(order);

  return (
    <Card
      onClick={() => toggleOrderDetailsModal(order)}
      classNames={{
        body: '!p-sm lg:!p-md',
      }}
      className={classnames('cursor-pointer ease-in-out transition-colors delay-50 duration-200', {
        'border-danger bg-danger/10 hover:bg-danger/20': isPendingConfirmation || isDeliveryDispatchFailed,
      })}
    >
      <Flex justify="space-between">
        <div className="space-y-xs">
          <Flex gap="small" align="center" wrap>
            <OrderMethodIcon order={order} className="my-auto" size={Sizes.sm} />
            <h5 className={classnames('my-auto', { 'text-danger': order.is_dummy })}>
              {order.is_dummy ? t('labels.dummy_order') : `#${order.number}`}
            </h5>
            {OrderHelpers.isOnSite(order) && <TablePill label={tableLabel(order)} />}
          </Flex>
          <p className="text-gray-500">
            {order.customer_first_name} {order.customer_last_name}
          </p>
          <p>
            {formatCurrency(order.total_amount)}
            {' • '}
            {t('labels.items', { count: collect(order.line_items).sum('quantity') })}
          </p>
          {order.customer_note && (
            <p className="text-gray-500 line-clamp-2 break-words" title={order.customer_note}>
              <MessageOutlined className="mr-xs" />
              {order.customer_note}
            </p>
          )}
        </div>
        <div className="flex flex-col gap-xs">
          {isPendingConfirmation && (
            <Tag color="#ef4343" className="rounded-2xl px-sm py-2xs mr-none w-fit animate-pulse lg:text-base text-sm">
              {t('labels.needs_acceptance')}
            </Tag>
          )}
          {isPendingDriver && (
            <Flex justify="end">
              <Tag color="#f59f0a" className="rounded-2xl px-sm py-2xs mr-none w-fit lg:text-base text-xs">
                <LoadingOutlined spin className="mr-xs" />
                {isDeliveryDispatchRetrying ? t('labels.driver_search_retrying') : t('labels.driver_pending')}
              </Tag>
            </Flex>
          )}
          {isDeliveryDispatchFailed && (
            <Flex justify="end">
              <Tag color="#ef4343" className="rounded-2xl px-sm py-2xs mr-none w-fit lg:text-base text-xs">
                <WarningOutlined className="mr-xs" />
                {t('labels.no_driver_found')}
              </Tag>
            </Flex>
          )}
          {hasPickupTime && (isScheduledOrderWithPendingPreparation || isPendingDriver) && (
            <Flex justify="end">
              <Tag color="#3b82f6" className="rounded-2xl px-sm py-2xs mr-none w-fit lg:text-base text-xs">
                <ClockCircleOutlined className="mr-xs" />
                {isToday ? t('labels.today') : pickupTime.toFormat('cccc, dd LLL')}
                {', '}
                {formatTime(order.estimated_pickup_time)}
              </Tag>
            </Flex>
          )}
          {isOngoing && isExternalDelivery && !isDeliveryDispatchFailed && (
            <Flex justify="end">
              <Tag color="var(--primary-color)" className="rounded-2xl px-sm py-nano mr-none w-fit lg:text-sm text-xs">
                <CheckCircleOutlined className="mr-xs" />
                {t('labels.driver_assigned')}
              </Tag>
            </Flex>
          )}
          {isOngoing && hasPickupTime && (
            <Flex justify="end">
              <Tag className="rounded-2xl px-sm py-2xs mr-none w-fit lg:text-base text-base font-semibold">
                <ClockCircleOutlined className="mr-xs" />
                {formatTime(order.estimated_pickup_time)}
              </Tag>
            </Flex>
          )}
          {isComplete && (
            <Flex justify="end">
              <Tag className="rounded-2xl px-sm py-2xs mr-none w-fit lg:text-base text-sm bg-primary/10 border border-primary/20 text-primary">
                {t('labels.complete')}
              </Tag>
            </Flex>
          )}
        </div>
      </Flex>
      {isPendingConfirmation && (
        <div className="mt-md text-danger">
          <LoadingOutlined spin className="mr-xs" />
          <Trans t={t}>text.order_not_confirmed</Trans>
        </div>
      )}
      {isDeliveryDispatchFailed && (
        <div className="mt-md text-danger">
          <WarningOutlined className="mr-xs" />
          <Trans t={t}>text.no_driver_found</Trans>
        </div>
      )}
      {isOngoing && (
        <Button
          onClick={(e) => {
            e.stopPropagation();
            toggleCompleteOrderModal(order);
          }}
          type="primary"
          className="mt-md w-full"
          size="large"
        >
          {t('buttons.complete_order')}
        </Button>
      )}
    </Card>
  );
}
